import './EsRegister.css';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { esLoginDestination } from './EsLoginDestination';
import { esOauthMessage } from './EsOauthMessage';
import { useState } from "react";
import { esFetch, esPrimedCsrfToken } from "../../common/EsFetch";
import { useScrollIntoViewWhen } from "../../common/EsScrollIntoView";
import { reservedNicknameRefusal } from "../../common/EsNicknames";
import EsBirthdateFields, { esBirthdateIso, esBirthdateRefusal } from "./EsBirthdateFields";

import {
    API_INVITE,
    API_REGISTER,
    EMAIL_PARAM,
    INVITE_PARAM,
    VIEW_LOGIN,
    VIEW_WELCOME
} from "../../common/EsConstants";
import { esHardNavigate, googleAuthUrl } from "../../common/EsEnv";
import { esFirstTouchSend } from "../../common/EsFirstTouch";

// For accountWarning -- the amber block EsForgot draws the identical fact in.
import './EsAccount.css';
import { useEsStrings } from '../../common/EsStrings';
import EsMinis from '../../common/EsMinis';

// esback's code for "already registered, logged in, moved to the code's group".
// ENUM_REGISTER_LOGGED_IN on the server.
const REGISTER_LOGGED_IN = 'loggedIn';

// esback's ENUM_REGISTER_BLOCKED: the birthdate is under the minimum age, so no account is made
// and the page keeps nothing that was typed (EsAge). esback's sentence is shown as it is.
const REGISTER_BLOCKED = 'blocked';

/**
 * A refusal that is not a failure, keyed on esback's own code.
 *
 * <p>THE SERVER SENDS A CODE AND THIS FILE SENDS A SENTENCE, the split
 * EsOauthMessage argues at length. There is one today: the address already has
 * an account and that account signs in with Google, so "an account for you@x.com
 * already exists" was true and useless -- there is no password to remember and
 * no reset that would mint one. Tim, 2026-09-22: "tell them to login with their
 * google account and they can add a password in their account."
 *
 * <p>Its remedy is the login page's: Login with Google there, then add a
 * password from the account.
 *
 * <p>An address that already has a PASSWORD account never reaches here --
 * EsAccountService.existingRowFor asks the pool and keeps XAccountExists for it.
 * Sending that person to Google would be false and would send them in a circle.
 */
const registerOutcome = ( t ) => ( {
    google:
        t( 'account-register-google-account', 'That address already has an Escapade account that signs in with Google. Login with Google, then add a password from your account if you want.' ),
} );

/**
 * The part of an address before the @, which is what a person answers to.
 *
 * <p>Module level rather than inside the component, because the initial state
 * uses it -- the register link at the foot of a joiner's mailing arrives
 * with the address already in the query string, and the nickname has to follow
 * it there exactly as it follows a typed one.
 */
const localPartOf = ( pEmail ) => {
    const aAt = ( pEmail || '' ).indexOf( '@' );
    return aAt > 0 ? pEmail.slice( 0, aAt ) : '';
};

export default function EsRegister() {
    const t = useEsStrings();
    // Hoisted above the error state, which now reads it. It used to sit beside
    // the form state below, which is still its main reader -- the invite code
    // and the address come off the query string too.
    const [searchParams] = useSearchParams();
    // The destination a sign-in from the Subscribe page carries (Tim, 2026-10-11):
    // read by the login link and by the landing after a register.
    const location = useLocation();

    // Seeded from ?oauthRefused=<reason>, which esback has been sending here
    // since the OAuth work landed and which nothing on this side read: a
    // refused Google sign-in dropped a reader on this page with no explanation
    // at all. See EsOauthMessage. Ordinary error state from here on, so
    // submitting the form below replaces it.
    const [error, setError] = useState( () => esOauthMessage( searchParams, t ) )

    // A refusal that is NOT a failure, kept apart because it is drawn amber and
    // for a reason: nothing has gone wrong, the account is fine, and red sends
    // somebody looking for what they mistyped. Held as { code, text } so a test
    // can tell a mapping that fell through from a sentence that was reworded.
    const [warning, setWarning] = useState( null );

    // See EsScrollIntoView: this form is the longest on the site and its
    // refusal sits above every field of it.
    const errorRef = useScrollIntoViewWhen( Boolean( error || warning ) );

    const formStatus = error ? <p ref={errorRef} data-testid="es-register-error" className="formError"> {error} </p>
                     : warning ? <p ref={errorRef} data-testid="es-register-warning" data-outcome={warning.code}
                                    className="accountWarning"> {warning.text} </p>
                             : <p data-testid="es-register-login-prompt" className="formStatus">
                                   { t( 'account-register-login-line', 'Already using Escapade? {login}', { login:
                                       <Link to={VIEW_LOGIN} state={ location.state } data-testid="es-register-login-link" className="whitespace-nowrap text-accent hover:underline">
                                          { t( 'account-register-login', 'Login' ) }
                                       </Link> } ) }
                               </p>
    const blankForm = {
        email: '',
        password: '',
        checkPassword: '',
        nickname: '',
        inviteCode: '',
        bday: '',
    };
    const navigate = useNavigate();
    // Seeded from ?invite=, so a handed-out code can arrive in the link rather
    // than off the back of a card. That is what the printed QR encodes.
    //
    // Read ONCE, as the initial value, the way EsCodexSearch seeds its box from
    // ?q=: this is a seed and not a binding. The field belongs to the person
    // from the first keystroke on, and a code they have deliberately cleared or
    // corrected must not be written back over them by a rerender.
    //
    // blankForm stays pure -- it is what handleSubmit resets to after a
    // successful registration, and a second account made in the same visit should
    // start empty rather than re-inherit a URL that is no longer on screen.
    const [form, setForm] = useState(
        () => ( {
            ...blankForm,
            inviteCode: searchParams.get( INVITE_PARAM ) ?? '',
            // Seeded from the "Register for Escapade" link at the foot of a
            // joiner's mailing, exactly as the invite code above is. They
            // already gave us this address; making them retype it off an email
            // is how a second account gets made with a typo in it instead of
            // the row they already have being finished.
            email: searchParams.get( EMAIL_PARAM ) ?? '',
            // And the nickname follows it, exactly as it would if they had
            // TYPED that address -- see handleChange below. Seeding the email
            // alone would leave the one field the follow-along exists to fill
            // sitting empty on the one form where the address arrived
            // pre-filled, so the person who least needed to type anything is
            // the only one who has to.
            nickname: localPartOf( searchParams.get( EMAIL_PARAM ) ?? '' ),
        } ) );

    // Whether the nickname is still following the email, or has been taken over.
    //
    // The field fills itself from the address as it is typed, so somebody who
    // never looks at it still gets a name -- and it is a name they recognise
    // rather than their address, which is the whole reason the nickname exists.
    // The moment they edit it themselves the following stops, because a field
    // that overwrote what you just typed would be worse than one that started
    // empty. It never resumes: one deliberate keystroke is enough to mean it.
    const [nicknameIsMine, setNicknameIsMine] = useState( false );

    const handleChange = (event) => {
        const { name, value } = event.target

        if ( name === 'nickname' ) {
            setNicknameError( null );
            setNicknameIsMine( true );
            setForm({ ...form, nickname: value });
            return;
        }

        if ( name === 'email' && !nicknameIsMine ) {
            setForm({ ...form, email: value, nickname: localPartOf( value ) });
            return;
        }

        setForm({ ...form, [name]: value })
    }

    function validateForm() {
        let errorText = "";

        // No nickname check. It fills itself from the address and the server
        // falls back to the same local part for a body that carries none, so
        // "required" would only ever fire on somebody who deliberately emptied
        // a field they are allowed to leave alone.
        if ( !form.email ) errorText += t( 'account-register-email-required', 'Email address is required.' ) + "\n";
        if ( !form.password ) errorText += t( 'account-register-password-required', 'Password is required.' ) + "\n";
        else if ( form.password.length < 10 )
            errorText += t( 'account-register-password-short', '{count, plural, one {Password must be at least # character.} other {Password must be at least # characters.}}', { count: 10 } ) + "\n";
        if ( form.password && (form.password !== form.checkPassword ) )
            errorText += t( 'account-register-password-mismatch', 'Password and Check Password do not match.' ) + "\n";
        // No invite-code check here, and that is still right. The code stopped
        // admitting anybody on 2026-09-15: it says which tier the account lands
        // on, and leaving it blank is a PUBLIC account rather than an error. A
        // "required" here would be the browser refusing a registration the
        // server is perfectly willing to make.
        //
        // A TYPED code that answers to nothing IS refused as of 2026-09-18 --
        // but by the server, which is the only side that knows the codes. It
        // comes back as msg on the 400 and handleSubmit puts it in this same
        // box. Nothing about it could be checked from in here without shipping
        // the list of codes to the browser, which would hand them to anybody who
        // opened the bundle.

        // Every registration gives one (EsAge); the age itself is esback's to judge.
        const aBirthdate = esBirthdateRefusal( form.bday, new Date(), t );
        if ( aBirthdate ) errorText += aBirthdate + "\n";

        setError( errorText );

        return ( !errorText );
    }

    /**
     * Register with Google: post the code, then leave for the hosted UI.
     *
     * THE ORDER IS THE WHOLE THING, and it is why the invitation box sits
     * outside the <form> and above both paths. A federated round trip is a
     * redirect, and a redirect carries no body of ours -- so the only moment
     * the code can be handed over is BEFORE the browser goes. esback stashes
     * the TIER it grants on the session and EsLoginSuccessHandler spends it on
     * the way back; see EsAuthController.acceptInvite.
     *
     * Awaited, and the navigation only happens after. Firing both and letting
     * them race would drop the tier whenever the redirect won, which is most of
     * the time on a fast connection -- and the failure is silent and looks like
     * a mistyped code.
     *
     * A BAD CODE STOPS THE TRIP AND A BROKEN CALL DOES NOT, which is the whole
     * of what sendInviteThenGo returns -- see its note. A code the server does
     * not recognise comes back as a message and the browser stays put, because
     * this screen is the only place that message can be read; a blip, a 500 or a
     * body that is not ours still goes, because registration is open and the
     * cost of a lost tier is not somebody's registration.
     *
     * Awaited for the same reason the function awaits internally: setError after
     * a navigation that has already been fired would be a state update on a page
     * that is leaving.
     */
    const registerWithGoogle = async () => {
        // The birthdate before the trip, as for a password: Google's own age is never used.
        const aBirthdate = esBirthdateRefusal( form.bday, new Date(), t );
        if ( aBirthdate ) {
            setError( aBirthdate );
            return;
        }

        const aRefusal = await sendInviteThenGo(
            form.inviteCode, await esPrimedCsrfToken(), esHardNavigate, esBirthdateIso( form.bday ),
            () => setForm( blankForm ) );

        if ( aRefusal ) setError( aRefusal );
    };

    const [ nicknameError, setNicknameError ] = useState( null );

    const handleSubmit = async (event) => {
        event.preventDefault();

        const aRefusal = await reservedNicknameRefusal( form.nickname );
        setNicknameError( aRefusal );
        if ( aRefusal ) return;

        // Cleared before the attempt, or the answer to the last address typed
        // sits under the form while this one is in flight -- and the two states
        // can only ever contradict each other.
        setWarning( null );

        if ( !validateForm() ) return;

        let aBodyJson = null;

        // Onto the session the register lands on, for the account it makes (EsConversions).
        await esFirstTouchSend();

        try {
            const aResponse = await esFetch( API_REGISTER, {
                method: 'POST',
                headers: {
                    'X-XSRF-TOKEN': await esPrimedCsrfToken(),
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify( { ...form, bday: esBirthdateIso( form.bday ) } ),
                credentials: 'include'
            } );

            const aBody = await aResponse.text();

            // Only parse what is actually JSON. A throw inside esback answers
            // Spring's whitelabel error page, and JSON.parse on HTML threw a
            // SyntaxError that went into setError -- so the screen showed React
            // error #31 rather than anything about the registration.
            if ( aBody ) {
                try {
                    aBodyJson = JSON.parse( aBody );
                }
                catch {
                    aBodyJson = null;
                }
            }

            // The CODE first: it is the only part of the answer both sides
            // agreed about, and esback's sentence beside it is for callers that
            // are not this page.
            // Under age: nothing typed stays on the page either.
            if ( aBodyJson?.outcome === REGISTER_BLOCKED ) {
                setForm( blankForm );
                setNicknameIsMine( false );
            }

            const aWarning = registerOutcome( t )[ aBodyJson?.outcome ];

            if ( aWarning ) setWarning( { code: aBodyJson.outcome, text: aWarning } );
            else if ( aBodyJson?.msg ) setError( aBodyJson.msg );
            else if ( !aResponse.ok ) setError( t( 'account-register-server-error', 'Sorry — the server could not sign you up (error {status}).', { status: String( aResponse.status ) } ) );
            else if ( aBodyJson?.outcome === REGISTER_LOGGED_IN ) {
                // The address already had an account, the password proved it, and
                // a code moved it: esback has logged them in. A full page load,
                // because AuthProvider reads the session once at mount.
                setForm( blankForm );
                esHardNavigate( esLoginDestination( location ) ?? VIEW_WELCOME );
            }
            else {
                setForm( blankForm );
                void navigate( VIEW_LOGIN, { state: location.state } );
            }
        }
        catch ( err ) {
            // err is an Error, and an Error cannot be rendered. Its message can.
            setError( err.message || t( 'common-server-unreachable', 'Sorry — could not reach the server.' ) );
        }
    }

    return (
        <section data-testid="es-register-page" className="relative z-10 pb-4 pt-nav-room">
            <EsMinis />
            <div className="container">
                <div className="flex flex-wrap mx-[-16px]">
                    <div className="w-full px-4">
                        <div className="mx-auto rounded-md p-12 max-w-[500px] sm:p-[60px]">
                            <h3 data-testid="es-register-heading" className="mb-3 text-center text-2xl font-bold text-heading sm:text-3xl">
                                { t( 'account-register-heading', 'Create an account' ) }
                            </h3>
                            <p data-testid="es-register-subheading" className="formSubHeader">
                                { t( 'account-register-blurb', 'No cost and spam-free' ) }
                            </p>
                            { /* STEP ONE, and it is first because of the order a person
                                 does things in rather than the order the fields are
                                 submitted in.
                                 
                                 It used to sit at the BOTTOM of the email form, below the
                                 passwords -- so somebody who meant to register with Google
                                 never saw it at all, and that is Abbey's point. It was a
                                 gate then, and a gate reached only down one of two paths is
                                 not a gate.
                                 
                                 IT IS NOT A GATE ANY MORE. Registration is open as of
                                 2026-09-15 and the code grants a tier -- see EsAccountRole.
                                 It stays at the top anyway, and for the same reason: both
                                 paths have to be able to carry it, and somebody who WAS
                                 given a code must not reach the end of the form having
                                 never been shown where to put it. A MISSED code costs them
                                 the alpha rather than the registration, which is a quieter
                                 failure and so a better reason to ask first, not a worse
                                 one.
                                 
                                 A MISTYPED one is no longer quiet at all. It was, until
                                 2026-09-18 -- a word nobody recognised registered happily at
                                 PUBLIC and said nothing -- and now the server refuses it and
                                 the message lands in the error box above these fields. The
                                 box is still optional and an empty one still registers; it is
                                 only a wrong answer that is now answered.
                                 
                                 It is outside the <form> and that is deliberate. The value
                                 lives in React state and handleSubmit serialises the whole
                                 form object, so the email path still sends it; being outside
                                 means the Google path can read the same one. */ }
                            <div className="mb-8">
                                <label className="mb-3 block text-sm font-medium text-heading" htmlFor="inviteCode">
                                    <span data-testid="es-register-step-one">{ t( 'account-register-step-one', 'Step 1 — Invitation Code' ) }</span>
                                    <span className="ms-2 text-xs font-normal text-aside">{ t( 'account-register-optional', 'optional' ) }</span>
                                </label>
                                { /* Controlled, so ?invite= can fill it. The nickname box
                                     below is the other one, and for the same kind of
                                     reason: both are boxes something else writes into.
                                     Without a value the seeded code would travel in the
                                     POST body while the box on screen sat empty, which
                                     reads as the link having failed and invites a retype
                                     over the top of a code that was already right. */ }
                                <input autoComplete="off"
                                    onChange={handleChange}
                                    type="text"
                                    id="inviteCode"
                                    name="inviteCode"
                                    value={form.inviteCode}
                                    data-testid="es-register-invite-code-input"
                                    className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus" />
                            </div>
                            { /* Before both ways in and outside the <form>, for the invite code's
                                 reason: Register with Google sends it through API_INVITE. */ }
                            <EsBirthdateFields idPrefix="es-register"
                                               bday={ form.bday }
                                               onChange={ ( pBday ) => setForm( { ...form, bday: pBday } ) } />
                            <p data-testid="es-register-step-two" className="mb-4 text-sm font-medium text-heading">
                                { t( 'account-register-step-two', 'Step 2 — choose how to log in' ) }
                            </p>
                            { /* type="button" because it is not in a form and must never
                                 become a submit by accident.
                                 
                                 NO LONGER DISABLED until there is a code. It was, back when
                                 a code was what admitted anybody; leaving it disabled would
                                 now stop a PUBLIC registration the server would happily accept,
                                 and would make the field read as required in the one place
                                 the label says it is not.
                                 
                                 WIRED 2026-09-17, and it found the code already in hand --
                                 which is what the ordering above was for. registerWithGoogle
                                 posts it to EsAuthController.acceptInvite and only then lets
                                 the browser leave; the comment that used to say "nothing is
                                 behind it yet" is what this replaces. */ }
                            <button type="button" data-testid="es-register-google-button"
                                    onClick={ registerWithGoogle } className="w-full flex items-center justify-center p-3 disabled:opacity-50 disabled:cursor-not-allowed bg-social-button text-body hover:text-accent text-base font-medium dark:hover:text-white rounded-md shadow-one dark:shadow-register mb-6">
                                <span className="me-3">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <g clipPath="url(#clip0_95:967)">
                                            <path d="M20.0001 10.2216C20.0122 9.53416 19.9397 8.84776 19.7844 8.17725H10.2042V11.8883H15.8277C15.7211 12.539 15.4814 13.1618 15.1229 13.7194C14.7644 14.2769 14.2946 14.7577 13.7416 15.1327L13.722 15.257L16.7512 17.5567L16.961 17.5772C18.8883 15.8328 19.9997 13.266 19.9997 10.2216" fill="#4285F4" />
                                            <path d="M10.2042 20.0001C12.9592 20.0001 15.2721 19.1111 16.9616 17.5778L13.7416 15.1332C12.88 15.7223 11.7235 16.1334 10.2042 16.1334C8.91385 16.126 7.65863 15.7206 6.61663 14.9747C5.57464 14.2287 4.79879 13.1802 4.39915 11.9778L4.27957 11.9878L1.12973 14.3766L1.08856 14.4888C1.93689 16.1457 3.23879 17.5387 4.84869 18.512C6.45859 19.4852 8.31301 20.0005 10.2046 20.0001" fill="#34A853"/>
                                            <path d="M4.39911 11.9777C4.17592 11.3411 4.06075 10.673 4.05819 9.99996C4.0623 9.32799 4.17322 8.66075 4.38696 8.02225L4.38127 7.88968L1.19282 5.4624L1.08852 5.51101C0.372885 6.90343 0.00012207 8.4408 0.00012207 9.99987C0.00012207 11.5589 0.372885 13.0963 1.08852 14.4887L4.39911 11.9777Z" fill="#FBBC05"/>
                                            <path d="M10.2042 3.86663C11.6663 3.84438 13.0804 4.37803 14.1498 5.35558L17.0296 2.59996C15.1826 0.901848 12.7366 -0.0298855 10.2042 -3.6784e-05C8.3126 -0.000477834 6.45819 0.514732 4.8483 1.48798C3.2384 2.46124 1.93649 3.85416 1.08813 5.51101L4.38775 8.02225C4.79132 6.82005 5.56974 5.77231 6.61327 5.02675C7.6568 4.28118 8.91279 3.87541 10.2042 3.86663Z" fill="#EB4335"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_95:967">
                                                <rect width="20" height="20" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </span>
                                { t( 'account-register-google', 'Register with Google' ) }
                            </button>
                            <div className="mb-8 flex items-center justify-center pt-4">
                                <span className="hidden w-full bg-section-rule max-w-[60px] h-[1px] sm:block"></span>
                                <p className="w-full px-5 text-center text-base font-medium text-body">{ t( 'account-register-or', 'Or use your own' ) }</p>
                                <span className="hidden w-full bg-section-rule max-w-[60px] h-[1px] sm:block"></span>
                            </div>

                            <form id="registerForm" data-testid="es-register-form" onSubmit={handleSubmit}>
                                <div className="mb-8">
                                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="email">
                                        {' '}
                                        { t( 'account-register-email', 'Email Address' ) }{' '}
                                    </label>
                                    <input autoComplete="email"
                                        onChange={handleChange}
                                        type="email"
                                        id="email"
                                        name="email"
                                        data-testid="es-register-email-input"
                                        className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus"
                                    />
                                </div>
                                <div className="mb-8">
                                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="password">
                                        {' '}{ t( 'account-register-password', 'Password' ) }{' '}
                                    </label>
                                    <input autoComplete="new-password"
                                        onChange={handleChange}
                                        type="password"
                                        id="password"
                                        name="password"
                                        data-testid="es-register-password-input"
                                        className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus" />
                                    <ul data-testid="es-register-password-rule" className="mt-2 list-disc ps-5 text-xs text-aside">
                                        <li>{ t( 'account-register-rule-length', 'At least 10 characters' ) }</li>
                                        <li>{ t( 'account-register-rule-upper', 'An uppercase letter' ) }</li>
                                        <li>{ t( 'account-register-rule-lower', 'A lowercase letter' ) }</li>
                                        <li>{ t( 'account-register-rule-number', 'A number' ) }</li>
                                        <li>{ t( 'account-register-rule-symbol', 'A symbol' ) }</li>
                                    </ul>
                                </div>
                                <div className="mb-8">
                                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="password">
                                        {' '}{ t( 'account-register-check-password', 'Check Password' ) }{' '}
                                    </label>
                                    <input autoComplete="new-password"
                                        onChange={handleChange}
                                        type="password"
                                        id="checkPassword"
                                        name="checkPassword"
                                        data-testid="es-register-check-password-input"
                                        className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus" />
                                </div>
                                { /* Last, and below the passwords, because it is
                                     the one field on this form that is already
                                     filled in by the time anybody reaches it --
                                     it follows the address as that is typed. Put
                                     first, it read as another thing to think of
                                     before registering; put here, it reads as what
                                     it is, an answer offered for changing.

                                     value, so it can be driven: this is the only
                                     controlled input on the form, and it has to
                                     be, because the email handler writes to it. */ }
                                <div className="mb-8">
                                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="nickname">
                                        {' '}{ t( 'account-register-nickname', 'Nickname' ) }{' '}
                                    </label>
                                    <input autoComplete="off"
                                        onChange={handleChange}
                                        type="text"
                                        id="nickname"
                                        name="nickname"
                                        value={form.nickname}
                                        data-testid="es-register-nickname-input"
                                        className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus" />
                                    <p data-testid="es-register-nickname-rule" className="mt-2 text-xs text-aside">
                                        { t( 'account-register-nickname-help', 'What other players see at the table.' ) }
                                    </p>
                                    { nicknameError &&
                                        <p data-testid="es-register-nickname-error" role="alert"
                                           className="mt-2 text-xs text-danger">{ nicknameError }</p> }
                                </div>
                                <div className="mb-6 pt-4">
                                    <button
                                        type="submit"
                                        data-testid="es-register-submit-button"
                                        className="buttonSubmit flex w-full items-center justify-center">
                                        { t( 'account-register-submit', 'Register' ) }
                                    </button>
                                </div>
                            </form>
                            { formStatus }
                        </div>
                    </div>
                </div>
            </div>
            <div className="absolute top-0 inset-x-0 z-[-1] overflow-hidden">
                <svg width="1440" height="969" viewBox="0 0 1440 969" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <mask id="mask0_95:1005" maskUnits="userSpaceOnUse" x="0" y="0" width="1440" height="969">
                        <rect width="1440" height="969" fill="#090E34" />
                    </mask>
                    <g mask="url(#mask0_95:1005)">
                        <path
                            opacity="0.1"
                            d="M1086.96 297.978L632.959 554.978L935.625 535.926L1086.96 297.978Z"
                            fill="url(#paint0_linear_95:1005)"
                        />
                        <path
                            opacity="0.1"
                            d="M1324.5 755.5L1450 687V886.5L1324.5 967.5L-10 288L1324.5 755.5Z"
                            fill="url(#paint1_linear_95:1005)"
                        />
                    </g>
                    <defs>
                        <linearGradient
                            id="paint0_linear_95:1005"
                            x1="1178.4"
                            y1="151.853"
                            x2="780.959"
                            y2="453.581"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop offset="0" stopColor="#4A6CF7" />
                            <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient
                            id="paint1_linear_95:1005"
                            x1="160.5"
                            y1="220"
                            x2="1099.45"
                            y2="1192.04"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop offset="0" stopColor="#4A6CF7" />
                            <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                </svg>
            </div>
        </section>
    );
}

/**
 * Hand the invitation code over, then leave for Cognito.
 *
 * <p>Lifted out of the component and exported because the ORDER is the only
 * interesting thing about it and the order is what has to be asserted. jsdom
 * cannot intercept a navigation -- window.location and its assign are
 * non-configurable and non-writable, so there is no way to watch the real thing
 * happen -- and a test that could not see the navigation could not see the
 * ordering either. Taking the navigate as an argument makes both observable
 * without the component having to know it is being tested.
 *
 * <p>AWAITED, and the navigate only after. Firing both and letting them race
 * drops the tier whenever the redirect wins, which is most of the time on a
 * fast connection, and the failure is silent and looks exactly like a mistyped
 * code.
 *
 * <p><b>A BAD CODE STOPS THE TRIP; A BROKEN CALL DOES NOT.</b> Those are two
 * different answers and this function's whole job is to tell them apart, which
 * is why it returns a message rather than nothing.
 *
 * <ul>
 *   <li><b>ok</b> -- go. The tier is on the session and Cognito is next.</li>
 *   <li><b>refused with a msg</b> -- the server read the code and does not
 *       recognise it (EsValidInviteCode, as of 2026-09-18). Return the message
 *       and do NOT navigate: a redirect here would take the person away from
 *       the only screen that could show it, and they would come back registered
 *       at PUBLIC never having seen a word about the code they typed. This is
 *       the case the function was silent about.</li>
 *   <li><b>anything else -- a throw, a 500, a body that is not ours</b> -- go
 *       anyway. Registration has been open since 2026-09-15 and the code only
 *       decides a TIER, so a network blip must not be the thing that stops
 *       somebody signing up. The cost is the tier, which is what a missing code
 *       costs anyway.</li>
 * </ul>
 *
 * <p><b>The success body carries a msg too</b>, which is the trap here:
 * acceptInvite answers 200 with {@code {msg: "Invitation accepted"}}. So the
 * refusal cannot be recognised the way the email form's does it -- "there is a
 * msg" -- and the response's own ok is what decides. Getting that backwards
 * would refuse every successful registration with a confirmation message.
 *
 * @param pCode  what is in the invitation box, possibly empty
 * @param pToken the XSRF cookie value
 * @param pGo    what to navigate with -- esHardNavigate in the app
 * @return the refusal to show, or null when the browser has been sent onward
 */
export async function sendInviteThenGo( pCode, pToken, pGo, pBday = null, pOnBlocked = null ) {
    // The same session the hosted UI comes back on, as the invite below.
    await esFirstTouchSend();

    try {
        const aResponse = await esFetch( API_INVITE, {
            method: 'POST',
            headers: {
                'X-XSRF-TOKEN': pToken,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            // credentials, because the session this writes to is the one the
            // redirect comes back on. Without it the attribute lands on a
            // session nobody returns to.
            // The birthdate rides along (EsAge): esback checks it before the trip and keeps
            // it for the account the trip comes back to make.
            body: JSON.stringify( { inviteCode: pCode, bday: pBday || null } ),
            credentials: 'include'
        } );

        if ( !aResponse.ok ) {
            // Only what is actually JSON, for the reason handleSubmit gives: a
            // throw inside esback answers Spring's whitelabel page, and
            // JSON.parse on HTML is a SyntaxError that would reach the screen
            // as React error #31 instead of anything about the code.
            let aMsg = null;
            let aOutcome = null;
            try {
                const aBody = await aResponse.text();
                const aJson = aBody ? JSON.parse( aBody ) : null;
                aMsg = aJson?.msg ?? null;
                aOutcome = aJson?.outcome ?? null;
            }
            catch {
                aMsg = null;
            }

            // Under age (EsAge): the page drops what was typed before it shows the sentence.
            if ( aOutcome === REGISTER_BLOCKED && pOnBlocked ) pOnBlocked();
            if ( aMsg ) return aMsg;
        }
    }
    catch {
        // See above: the tier is lost, the registration is not.
    }

    pGo( googleAuthUrl() );
    return null;
}
